import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { canonAPI } from '../lib/api';
import { 
  Search as SearchIcon, 
  BookOpen,
  FileText,
  Shield,
  ChevronRight,
  Loader2
} from 'lucide-react';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    try {
      const response = await canonAPI.search(query);
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalResults = results 
    ? results.articles.length + results.glossary.length + results.principles.length 
    : 0;

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="search-page">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Search the Canon
        </h1>
        <p className="text-muted-foreground">
          Find articles, principles, and definitions
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
        <div className="flex space-x-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for articles, terms, principles..."
              className="pl-10 h-12 text-lg"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="h-12 px-6" data-testid="search-button">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              'Search'
            )}
          </Button>
        </div>
      </form>

      {/* Results */}
      {results && (
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "{query}"
            </p>
          </div>

          {totalResults === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <SearchIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-muted-foreground">No results found for "{query}"</p>
                <p className="text-sm text-muted-foreground mt-1">Try different keywords</p>
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="all" className="space-y-4">
              <TabsList>
                <TabsTrigger value="all">All ({totalResults})</TabsTrigger>
                <TabsTrigger value="articles">Articles ({results.articles.length})</TabsTrigger>
                <TabsTrigger value="glossary">Glossary ({results.glossary.length})</TabsTrigger>
                <TabsTrigger value="principles">Principles ({results.principles.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-4" data-testid="search-results-all">
                {/* Articles */}
                {results.articles.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2 flex items-center">
                      <BookOpen className="h-4 w-4 mr-2" />
                      Articles
                    </h3>
                    <div className="space-y-2">
                      {results.articles.slice(0, 3).map((article) => (
                        <Link
                          key={article.number}
                          to={`/article/${article.number}`}
                          className="search-result block"
                          data-testid={`result-article-${article.number}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <Badge variant="outline" className="mb-1">Article {article.number}</Badge>
                              <h4 className="font-medium">{article.title}</h4>
                              <p className="text-sm text-muted-foreground line-clamp-1">{article.content}</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Glossary */}
                {results.glossary.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Glossary Terms
                    </h3>
                    <div className="space-y-2">
                      {results.glossary.slice(0, 3).map((term) => (
                        <Link
                          key={term.term}
                          to="/glossary"
                          className="search-result block"
                          data-testid={`result-term-${term.term}`}
                        >
                          <h4 className="font-medium">{term.term}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{term.definition}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Principles */}
                {results.principles.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2 flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      Principles
                    </h3>
                    <div className="space-y-2">
                      {results.principles.map((principle) => (
                        <Link
                          key={principle.name}
                          to={`/library?principle=${principle.name.toLowerCase()}`}
                          className="search-result block"
                          data-testid={`result-principle-${principle.name}`}
                        >
                          <h4 className="font-medium">{principle.name}</h4>
                          <p className="text-sm text-muted-foreground">{principle.description}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="articles" className="space-y-2" data-testid="search-results-articles">
                {results.articles.map((article) => (
                  <Link
                    key={article.number}
                    to={`/article/${article.number}`}
                    className="search-result block"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <Badge variant="outline">Article {article.number}</Badge>
                          <Badge variant="secondary" className="text-xs">{article.title_name}</Badge>
                        </div>
                        <h4 className="font-medium">{article.title}</h4>
                        <p className="text-sm text-muted-foreground">{article.content}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </TabsContent>

              <TabsContent value="glossary" className="space-y-2" data-testid="search-results-glossary">
                {results.glossary.map((term) => (
                  <Card key={term.term} className="glossary-term">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{term.term}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{term.definition}</p>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="principles" className="space-y-2" data-testid="search-results-principles">
                {results.principles.map((principle) => (
                  <Link
                    key={principle.name}
                    to={`/library?principle=${principle.name.toLowerCase()}`}
                    className="search-result block"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{principle.name}</h4>
                        <p className="text-sm text-muted-foreground">{principle.description}</p>
                        <p className="text-xs text-accent mt-1">
                          {principle.related_articles.length} related articles
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </div>
      )}

      {/* Initial State */}
      {!results && !loading && (
        <div className="max-w-2xl mx-auto text-center py-12">
          <SearchIcon className="h-16 w-16 mx-auto mb-4 opacity-20" />
          <p className="text-muted-foreground">
            Enter a search term to find articles, glossary terms, and principles
          </p>
        </div>
      )}
    </div>
  );
}
